import { Injectable } from '@angular/core';
import { GoogleAuthProvider } from 'firebase/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular-ivy';
import { FirebaseService } from './firebase.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user: any;
  public isLoggedUser: boolean;

  constructor(
    private firebaseService: FirebaseService,
    public afAuth: AngularFireAuth,
    private http: HttpClient
  ) {
    /* Saving user data in localstorage when
    logged in and setting up null when logged out */
    this.afAuth.authState.subscribe(async (user) => {
      if (user) {
        setTimeout(() => {
          this.isLoggedUser = true;
        }, 1000);
        localStorage.setItem('user', JSON.stringify(user));
        Sentry.setUser({ id: user.uid, email: user.email ?? undefined });
        if (!this.user) {
          await this.updateUserFromDB(user);
        }
      } else {
        this.isLoggedUser = false;
        localStorage.removeItem('user');
        Sentry.setUser(null);
      }
    });
  }

  // Get user from DB and save data to storage
  public updateUserFromDB(user: any) {
    // Get user from DB to store locally
    this.firebaseService
      .getDocument(`/users/${user.uid}`)
      .then(async (u: any) => {
        if (!u) {
          return; // first time login, the user does not exist in the DB
        }
        this.firebaseService
          .updateDocument('/users', user.uid, {
            last_login: new Date().getTime(),
          })
          .then(async (_) => {
            u.uid = user.uid;
            this.user = u;
            localStorage.setItem('userInfo', JSON.stringify(this.user));
            Sentry.setUser({ id: user.uid, email: u.email ?? user.email ?? undefined, role: u.role });
          });
      });
  }

  // Sign in email and password
  public emailAuth(data: any) {
    return new Promise<any>((resolve, reject) => {
      this.afAuth.signInWithEmailAndPassword(data.email, data.password).then(
        async (res) => {
          console.log(res.user);
          if (!res.user) {
            return;
          }
          let user;
          const approvedUser = await this.isUserAuthorized(res.user);

          if (approvedUser) {
            user = {
              ...res.user.providerData[0],
              uid: res.user.uid,
              approved: approvedUser.approved,
              emailVerified: res.user.emailVerified,
            };
          } else {
            user = {
              ...res.user.providerData[0],
              uid: res.user.uid,
              approved: false,
              emailVerified: res.user.emailVerified,
            };
          }

          resolve(user);
        },
        (err) => reject(err)
      );
    });
  }

  // Sign in with Google
  public googleAuth() {
    return this.afAuth
      .signInWithPopup(new GoogleAuthProvider())
      .then(async (res) => {
        console.log(res.user);
        if (!res.user) {
          return null;
        }
        let user;
        const approvedUser = await this.isUserAuthorized(res.user);
        if (approvedUser) {
          user = {
            ...res.user.providerData[0],
            uid: res.user.uid,
            approved: approvedUser.approved,
          };
        }
        // No value in 'authorized_users' = the user logs-in with goole for the first time
        else {
          // Autorize the user
          this.firebaseService.updateDocument(
            '/authorized_users',
            res.user.uid,
            true
          );
          user = {
            ...res.user.providerData[0],
            uid: res.user.uid,
            approved: true,
          };
        }

        return user;
      })
      .catch((error) => {
        console.log(error);
      });
  }

  public register(value: any) {
    return new Promise<any>((resolve, reject) => {
      this.afAuth
        .createUserWithEmailAndPassword(value.email, value.password)
        .then(
          (res) => resolve(res),
          (err) => reject(err)
        );
    });
  }

  public async sendVerificationMail() {
    return this.afAuth.currentUser
      .then((u: any) => u.sendEmailVerification())
      .then(() => {
        console.log('email send');
      });
  }

  public async forgotPassword(passwordResetEmail: string) {
    try {
      await this.afAuth.sendPasswordResetEmail(passwordResetEmail);
      return { success: true };
    } catch (error: any) {
      let message;
      if (error.code === 'auth/user-not-found') {
        message = 'The email address was not found.';
      }
      if (error.code === 'auth/invalid-email') {
        message = 'Invalid email';
      }
      return { success: false, message };
    }
  }

  public isUserAuthorized(user: any) {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocument('/authorized_users/' + user.uid).then(
        (res: any) => {
          resolve(res);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }

  public async logout() {
    localStorage.removeItem('user');
    localStorage.clear();
    await this.afAuth.signOut();
  }

  public async getUser() {
    const user = localStorage.getItem('user');
    return user;
  }

  public getCurrentAuthData = async (forceRefresh: boolean = false): Promise<{ email: string; idToken: string }> => {
    const currentUser = await this.afAuth.currentUser;
    return {
      email: currentUser!.email!,
      idToken: await currentUser!.getIdToken(forceRefresh),
    };
  };

  public loginWithApiToken = async (email: string, idToken: string): Promise<any> => {
    const { token } = await lastValueFrom(this.http.post<any>(`${environment.apiUrl}/auth/login/token`, { email, idToken }));
    const authUser = (await this.afAuth.signInWithCustomToken(token)).user;

    if (!authUser) {
      throw new Error('auth/user-not-found');
    }

    const approvedUser = await this.isUserAuthorized(authUser);

    return {
      ...authUser.providerData[0],
      uid: authUser.uid,
      approved: approvedUser?.approved,
      emailVerified: authUser.emailVerified,
    };
  };

  async isConnected() {
    const user = localStorage.getItem('user');
    return !!user;
  }

  public canAccessDashboard = async (): Promise<boolean> => {
    const authUser = JSON.parse(localStorage.getItem('user') || 'null');
    if (!authUser?.uid) {
      return false;
    }

    const user = await this.firebaseService.getDocument(`/users/${authUser.uid}`);
    if (!user) {
      return false;
    }

    return !!(user?.firstName && user?.lastName && user?.gender && user?.birthday)
      && ['admin', 'manager'].includes(user.role);
  }

  public getUserRole = (): string => {
    const user = localStorage.getItem('userInfo');
    const jsonUser = JSON.parse(user);
    return jsonUser?.role;
  }
}
